import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {type RefCallback, useCallback, useLayoutEffect, useMemo, useRef} from 'react'

/**
 * Measure the content height of a textarea as a CSS `calc()` expression (scrollHeight minus vertical
 * padding). Kept as a module-scope helper so it carries no reactive dependencies and the effects below
 * can stay referentially stable.
 */
function measureContentHeight(element: HTMLTextAreaElement): string {
  const scrollHeight = element.scrollHeight
  const {paddingTop, paddingBottom} = getComputedStyle(element)
  return `calc(${scrollHeight}px - ${paddingTop} - ${paddingBottom})`
}

type UseDynamicTextareaHeightSettings = {
  disabled?: boolean
  minHeightLines?: number
  maxHeightLines?: number
  /** The current value of the input. */
  value: string
}

/**
 * Calculates the optimal height of the textarea according to its content, automatically
 * resizing it as the user types. If the user manually resizes the textarea, their setting
 * will be respected.
 *
 * Returns a `ref` to attach to the `textarea` together with static styles to spread onto its `style`
 * prop. If you already keep your own ref on the element, combine them with `useMergedRefs` from
 * `@primer/react`. If you are using Primer's `Textarea`, both go on the child `textarea` element.
 *
 * On browsers without `field-sizing: content` (e.g. Firefox before 152 or Safari before 26.2), the
 * dynamic height is measured and written directly to the textarea's `style.height` instead of being
 * returned, so resizing never re-renders the consuming component. The returned `ref` lets the hook
 * measure at the exact moment the textarea attaches — including Slots-delayed mounts — so no post-paint
 * effect is needed.
 *
 * NOTE: for the most accurate results, be sure that the `lineHeight` of the element is
 * explicitly set in CSS.
 */
export const useDynamicTextareaHeight = ({
  disabled,
  minHeightLines,
  maxHeightLines,
  value,
}: UseDynamicTextareaHeightSettings): {
  ref: RefCallback<HTMLTextAreaElement>
  minHeight: string | undefined
  maxHeight: string | undefined
  boxSizing: 'content-box'
  fieldSizing: 'content'
  overflowWrap: 'anywhere'
} => {
  const {isBrowser} = useRuntimeEnvironment()
  const supportsFieldSizingContent = useMemo(() => {
    try {
      return isBrowser ? CSS.supports('field-sizing', 'content') : true
    } catch {
      // If the browser doesn't support CSS.supports, we'll just assume it doesn't support `field-sizing: content` and fall back to our manual height calculations.
      return false
    }
  }, [isBrowser])

  // We only need to measure and apply an explicit height on browsers that lack `field-sizing: content`.
  // When it's supported (the common case on modern browsers), the textarea auto-sizes natively via the
  // returned `fieldSizing` style, so there is nothing to measure.
  const needsManualMeasurement = !disabled && !supportsFieldSizingContent

  // Only re-measure (on every keystroke) when manual measurement is actually required. When it isn't,
  // this resolves to a constant `null`, so the layout effect below runs once and never re-runs while
  // typing — no per-keystroke layout effect, reflow, or commit.
  const measuredValue = needsManualMeasurement ? value : null

  // The textarea, captured by the returned callback `ref` below (rather than supplied by the consumer)
  // so the hook learns the exact moment it attaches and detaches.
  const elementRef = useRef<HTMLTextAreaElement | null>(null)
  // Handle for the coalescing animation frame (see the layout effect) so it can be cancelled.
  const frameRef = useRef(0)
  // Whether a real (non-empty) height has already been applied. Drives the "first measurement is
  // synchronous, the rest are deferred" behavior below, and resets when the field is emptied.
  const hasAppliedRef = useRef(false)
  // The element most recently measured on attach. Guards the callback ref so it measures only once per
  // attached element, even if a non-memoized ref merge re-invokes it on later renders.
  const measuredElementRef = useRef<HTMLTextAreaElement | null>(null)

  // Stable across keystrokes (its deps don't change while typing): it self-gates on the feature-support /
  // disabled flags and reads the live element value. The measured height is written straight to the DOM
  // rather than through React state, so growing the textarea never triggers a re-render/commit of the
  // consuming component on the legacy path.
  const measure = useCallback(() => {
    if (disabled || supportsFieldSizingContent) return

    const element = elementRef.current
    if (!element) return

    if (element.value) {
      element.style.height = measureContentHeight(element)
      hasAppliedRef.current = true
    } else {
      // Empty input: collapse back to the intrinsic (min) height instead of retaining the last measured
      // height, matching how native `field-sizing: content` behaves.
      element.style.height = ''
      hasAppliedRef.current = false
    }
  }, [disabled, supportsFieldSizingContent])

  // Callback ref: fires exactly when the textarea attaches or detaches. Measuring on attach (rather than
  // from a post-paint effect that guesses the element has mounted) sizes the field synchronously the
  // moment it exists, covering Slots-delayed mounts precisely. The `measuredElementRef` guard keeps this
  // to one measurement per attached element if a non-memoized ref merge re-invokes it on later renders.
  const ref = useCallback<RefCallback<HTMLTextAreaElement>>(
    element => {
      elementRef.current = element
      if (element === null) {
        cancelAnimationFrame(frameRef.current)
        return
      }
      if (element !== measuredElementRef.current) {
        measuredElementRef.current = element
        measure()
      }
    },
    [measure],
  )

  useLayoutEffect(() => {
    // `null` means manual measurement isn't needed (field-sizing supported, or disabled), so this effect
    // runs once and never again while typing.
    if (measuredValue === null) return

    if (!hasAppliedRef.current) {
      // First content after the field was empty (e.g. a multi-line paste): apply synchronously before
      // paint so it doesn't briefly flash at a single row.
      measure()
      return
    }

    // Subsequent measurements while typing: defer to the next animation frame so the layout-forcing
    // `scrollHeight` read happens after paint — off the keystroke's critical path — and rapid keystrokes
    // coalesce into a single measurement. Tradeoff: on the legacy path the height grows one frame
    // (~16ms) after a line is added or removed. Modern browsers never reach this path (native field-sizing).
    cancelAnimationFrame(frameRef.current)
    frameRef.current = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(frameRef.current)
  }, [measure, measuredValue])

  return {
    ref,
    minHeight: minHeightLines ? `${minHeightLines}lh` : undefined,
    maxHeight: maxHeightLines ? `${maxHeightLines}lh` : undefined,
    boxSizing: 'content-box',
    fieldSizing: 'content',
    // `overflow-wrap: anywhere` should be the default behavior for textareas, but `field-sizing: content` appears to
    // break that expectation. To ensure that long words still break instead of expanding the textarea horizontally,
    // we must set this explicitly.
    overflowWrap: 'anywhere',
  }
}
